import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import './TimeSheets.css';
import { FaPlus, FaEdit, FaEye, FaChevronDown, FaSearch } from 'react-icons/fa';
import { IoMdClose } from 'react-icons/io';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getTimesheets } from '../../api/timesheets';
import { useTimesheet } from './context/TimesheetContext';

const formatLocalDate = (d) =>
  d
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
        d.getDate()
      ).padStart(2, '0')}`
    : '';

const formatPeriod = (fromDate, toDate) => {
  if (fromDate && toDate) return `${fromDate} – ${toDate}`;
  return fromDate || toDate || '';
};

const matchesPeriod = (sheet, query) => {
  const q = query.toLowerCase();
  return (
    String(sheet.fromDate ?? '').toLowerCase().includes(q) ||
    String(sheet.toDate ?? '').toLowerCase().includes(q) ||
    formatPeriod(sheet.fromDate, sheet.toDate).toLowerCase().includes(q)
  );
};

/**
 * Least important first — hidden as the *table container* shrinks
 * (accounts for open sidebar / content padding, not raw viewport).
 * Always kept: status, period, total, actions.
 */
const COLUMN_PRIORITY = [
  { key: 'ot', labelKey: 'ot', className: 'col-p5', maxWidth: 1100 },
  { key: 'st', labelKey: 'st', className: 'col-p4', maxWidth: 980 },
  { key: 'approver', labelKey: 'approver', className: 'col-p3', maxWidth: 860 },
  { key: 'project', labelKey: 'project', className: 'col-p2', maxWidth: 740 },
  { key: 'id', labelKey: 'id', className: 'col-p1', maxWidth: 620 },
];

const TimeSheets = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const { startDate, setStartDate, endDate, setEndDate } = useTimesheet();

  const [timeSheets, setTimeSheets] = useState([]);
  const [filteredSheets, setFilteredSheets] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'descending' });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInputs, setSearchInputs] = useState({});
  const [searchVisible, setSearchVisible] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hiddenColumnKeys, setHiddenColumnKeys] = useState([]);
  const tableContainerRef = useRef(null);

  const rowsPerPage = 5;
  const orgId = user?.department?.costCenter?.organization?.orgId ?? 1;
  const userId = user?.userId ?? user?.id;

  useEffect(() => {
    document.documentElement.setAttribute('lang', i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return undefined;

    const syncFromWidth = (width) => {
      const nextHidden = COLUMN_PRIORITY.filter((col) => width <= col.maxWidth).map(
        (col) => col.key
      );
      setHiddenColumnKeys((prev) =>
        prev.length === nextHidden.length && prev.every((key, i) => key === nextHidden[i])
          ? prev
          : nextHidden
      );
    };

    syncFromWidth(el.clientWidth);

    if (typeof ResizeObserver !== 'function') {
      const onResize = () => syncFromWidth(el.clientWidth);
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      const width = entry?.contentRect?.width ?? el.clientWidth;
      syncFromWidth(width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const fetchTimeSheets = useCallback(
    async (from = '', to = '') => {
      if (!userId) {
        setError(t('timesheets.loadFailed'));
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const rows = await getTimesheets({
          orgId,
          userId,
          from,
          to,
        });
        setTimeSheets(rows);
        setFilteredSheets(null);
        setCurrentPage(1);
      } catch (err) {
        console.error('Error fetching timesheets:', err);
        setTimeSheets([]);
        setFilteredSheets(null);
        const status = err?.response?.status;
        setError(
          status
            ? `${t('timesheets.loadFailed')} (${status})`
            : t('timesheets.loadFailed')
        );
      } finally {
        setLoading(false);
      }
    },
    [userId, orgId, t]
  );

  useEffect(() => {
    const navFilters = location.state?.filters;
    if (navFilters?.from) {
      setStartDate(new Date(navFilters.from + 'T00:00:00'));
    }
    if (navFilters?.to) {
      setEndDate(new Date(navFilters.to + 'T00:00:00'));
    }

    const from = navFilters?.from || formatLocalDate(startDate) || '';
    const to = navFilters?.to || formatLocalDate(endDate) || '';
    fetchTimeSheets(from, to);
    // Mount / auth change only — date Search button refetches; do not re-run when
    // location.state is cleared or dates change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchTimeSheets]);

  const sortedTimeSheets = useMemo(() => {
    const sortableItems = filteredSheets ? [...filteredSheets] : [...timeSheets];

    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'approver') {
          aValue = aValue || '';
          bValue = bValue || '';
        }

        if (sortConfig.key === 'standardTime' || sortConfig.key === 'overtime' || sortConfig.key === 'total') {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        }

        if (sortConfig.key === 'period') {
          aValue = a.fromDate ? new Date(a.fromDate) : new Date(0);
          bValue = b.fromDate ? new Date(b.fromDate) : new Date(0);
          if (aValue.getTime() === bValue.getTime()) {
            aValue = a.toDate ? new Date(a.toDate) : new Date(0);
            bValue = b.toDate ? new Date(b.toDate) : new Date(0);
          }
        }

        if (typeof aValue === 'string') aValue = aValue.toLowerCase();
        if (typeof bValue === 'string') bValue = bValue.toLowerCase();

        if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }

    return sortableItems;
  }, [timeSheets, filteredSheets, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedTimeSheets.length / rowsPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * rowsPerPage;
  const currentRows = sortedTimeSheets.slice(startIndex, startIndex + rowsPerPage);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handlePreviousPage = () => {
    if (safeCurrentPage > 1) setCurrentPage(safeCurrentPage - 1);
  };

  const handleNextPage = () => {
    if (safeCurrentPage < totalPages) setCurrentPage(safeCurrentPage + 1);
  };

  const requestSort = (key) => {
    let direction = 'descending';
    if (sortConfig.key === key && sortConfig.direction === 'descending') {
      direction = 'ascending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return (
        <span className="table-sort-arrows">
          <FaChevronDown className="table-sort-arrow up faded margin-bottom" />
          <FaChevronDown className="table-sort-arrow faded margin-top" />
        </span>
      );
    }
    const isAsc = sortConfig.direction === 'ascending';
    return (
      <FaChevronDown className={`table-sort-arrow centered${isAsc ? ' up' : ''}`} />
    );
  };

  const handleAdd = () => {
    navigate('/timesheets/new');
  };

  const handleEdit = (id) => {
    navigate(`/timesheets/edit/${id}`);
  };

  const handleView = (id) => {
    navigate(`/timesheets/edit/${id}?viewOnly=true`);
  };

  const handleApplyFilter = () => {
    fetchTimeSheets(formatLocalDate(startDate), formatLocalDate(endDate));
  };

  const handleSearchIconClick = (key) => {
    setSearchVisible((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSearchInputChange = (key, value) => {
    setSearchInputs((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearchInputKeyDown = (key, e) => {
    if (e.key === 'Enter') {
      handleSearch(key);
    } else if (e.key === 'Escape') {
      setSearchVisible((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleSearch = (key) => {
    const value = (searchInputs[key] || '').toLowerCase();
    setFilteredSheets(
      timeSheets.filter((sheet) =>
        key === 'period'
          ? matchesPeriod(sheet, value)
          : String(sheet[key] ?? '').toLowerCase().includes(value)
      )
    );
    setCurrentPage(1);
    setSearchVisible((prev) => ({ ...prev, [key]: false }));
  };

  const handleCloseSearch = (key) => {
    setSearchVisible((prev) => ({ ...prev, [key]: false }));
  };

  const renderSortableHeader = (key, titleKey, className = '') => (
    <th className={`table-header sortable${className ? ` ${className}` : ''}`}>
      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <span
          onClick={() => requestSort(key)}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
        >
          {t(`timesheets.${titleKey}`)} {getSortIcon(key)}
        </span>
        <span style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          {searchVisible[key] && (
            <div className="table-search-popover">
              <input
                type="text"
                className="table-search-input"
                value={searchInputs[key] || ''}
                autoFocus
                onChange={(e) => handleSearchInputChange(key, e.target.value)}
                onKeyDown={(e) => handleSearchInputKeyDown(key, e)}
                placeholder={t('timesheets.searchPlaceholder', {
                  field: t(`timesheets.${titleKey}`),
                })}
              />
              <div className="table-search-btn-row">
                <button className="table-search-btn" onClick={() => handleSearch(key)}>
                  <FaSearch style={{ fontSize: '16px' }} />
                </button>
                <button className="table-search-close-btn" onClick={() => handleCloseSearch(key)}>
                  <IoMdClose style={{ fontSize: '18px' }} />
                </button>
              </div>
            </div>
          )}
          <FaSearch
            className="table-search-icon"
            style={{ cursor: 'pointer', marginLeft: '4px', fontSize: '14px' }}
            onClick={() => handleSearchIconClick(key)}
          />
        </span>
      </span>
    </th>
  );

  const hiddenColumns = COLUMN_PRIORITY.filter((col) =>
    hiddenColumnKeys.includes(col.key)
  );
  const tableClassName = [
    'timesheets-table',
    ...hiddenColumns.map((col) => `hide-${col.className}`),
  ].join(' ');

  const renderRowActions = (sheet) => {
    const canEdit = sheet.status === 'draft' || sheet.status === 'rejected';
    const canView = sheet.status === 'pending' || sheet.status === 'approved';

    return (
      <div className="actions-cell-inner">
        <div className="actions-inline">
          {canEdit && (
            <button className="action-button edit" onClick={() => handleEdit(sheet.id)}>
              <FaEdit />
              {t('timesheets.edit')}
            </button>
          )}
          {canView && (
            <button className="action-button edit" onClick={() => handleView(sheet.id)}>
              <FaEye />
              {t('timesheets.view')}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="timesheets-container">
      {error && <div className="timesheets-error">{error}</div>}

      <div className="timesheets-header">
        <h1 className="timesheets-title">{t('timesheets.title')}</h1>
      </div>

      <div className="filters-wrapper">
        <div className="filters-container">
          <div className="filter-controls">
            <div className="filter-dates-row">
              <div className="filter-group">
                <label className="filter-label">{t('timesheets.from')}</label>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => setStartDate(date)}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  placeholderText={t('timesheets.selectStartDate')}
                  className="date-input"
                />
              </div>
              <div className="filter-group">
                <label className="filter-label filter-label-to">
                  {t('timesheets.to')}
                </label>
                <DatePicker
                  selected={endDate}
                  onChange={(date) => setEndDate(date)}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate}
                  placeholderText={t('timesheets.selectEndDate')}
                  className="date-input"
                />
              </div>
            </div>
            <button
              className="apply-filter-button"
              onClick={handleApplyFilter}
              disabled={loading}
            >
              {t('timesheets.search')}
            </button>
          </div>
        </div>

        <div className="add-button-container">
          <button className="add-button" onClick={handleAdd}>
            <FaPlus />
            {t('timesheets.add')}
          </button>
        </div>
      </div>

      <div className="table-container" ref={tableContainerRef}>
        <table className={tableClassName}>
          <thead>
            <tr>
              {renderSortableHeader('status', 'status')}
              {renderSortableHeader('id', 'id', 'col-p1')}
              {renderSortableHeader('period', 'period')}
              {renderSortableHeader('approver', 'approver', 'col-p3')}
              {renderSortableHeader('project', 'project', 'col-p2')}
              {renderSortableHeader('standardTime', 'st', 'col-p4')}
              {renderSortableHeader('overtime', 'ot', 'col-p5')}
              {renderSortableHeader('total', 'total')}
              <th className="table-header">{t('timesheets.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="table-cell timesheets-loading" colSpan={9}>
                  {t('timesheets.loading')}
                </td>
              </tr>
            ) : currentRows.length === 0 ? (
              <tr>
                <td className="table-cell" colSpan={9} style={{ textAlign: 'center' }}>
                  {t('timesheets.noData')}
                </td>
              </tr>
            ) : (
              currentRows.map((sheet) => (
                <tr key={sheet.id}>
                  <td className="table-cell">
                    <span className={`status-badge ${sheet.status}`}>
                      {t(
                        `timesheet.${sheet.status}`,
                        sheet.status.charAt(0).toUpperCase() + sheet.status.slice(1)
                      )}
                    </span>
                  </td>
                  <td className="table-cell col-p1">{sheet.id}</td>
                  <td className="table-cell period-cell">
                    {formatPeriod(sheet.fromDate, sheet.toDate)}
                  </td>
                  <td className="table-cell col-p3">
                    {['approved', 'rejected'].includes(sheet.status) ? sheet.approver : '-'}
                  </td>
                  <td className="table-cell col-p2">{sheet.project}</td>
                  <td className="table-cell col-p4">{sheet.standardTime}</td>
                  <td className="table-cell col-p5">{sheet.overtime}</td>
                  <td className="table-cell">{sheet.total}</td>
                  <td className="table-cell actions-cell">{renderRowActions(sheet)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <button
          onClick={handlePreviousPage}
          disabled={safeCurrentPage === 1}
          className="pagination-arrow"
          aria-label={t('timesheets.previous')}
        >
          &lt;
        </button>
        <input
          type="number"
          min={1}
          max={totalPages}
          value={safeCurrentPage}
          onChange={(e) => {
            let page = Number(e.target.value);
            if (page < 1) page = 1;
            if (page > totalPages) page = totalPages;
            setCurrentPage(page);
          }}
          className="pagination-input"
        />
        <span className="pagination-total">
          {t('timesheets.of')} {totalPages}
        </span>
        <button
          onClick={handleNextPage}
          disabled={safeCurrentPage === totalPages}
          className="pagination-arrow"
          aria-label={t('timesheets.next')}
        >
          &gt;
        </button>
      </div>
    </div>
  );
};

export default TimeSheets;
